import { Card, CardBody, Sparkline, ProgressBar, NarrativeCard } from "@/design/components";
import { formatPct } from "@/design/format";
import { useSeed, useMoney } from "@/domain/hooks";
import { railExposure } from "@/domain/rails";

/**
 * Traceable-rails share: how much spend moves on mobile money, bank and card versus cash,
 * trending up as the pilot shrinks cash (pilot-readiness addendum §4.3). The deterrence model
 * is that cash stays small and each cash spend is individually accountable.
 */
export function CashExposureCard({ compact = false }: { compact?: boolean }) {
  const seed = useSeed();
  const money = useMoney();
  const r = railExposure(seed);

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-ink">Traceable-rails share</p>
            <p className="text-xs text-faint">Mobile money, bank and card vs cash</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-semibold tabular-nums text-ink">{formatPct(r.share)}</p>
            <p className="text-[11px] text-faint">target {formatPct(r.target)}</p>
          </div>
        </div>

        <div>
          <ProgressBar value={r.share} className="h-2" />
          <div className="mt-1 flex items-center justify-between text-[11px] text-faint">
            <span className="text-ok">Traceable {money(r.traceable)}</span>
            <span className={r.cash > 0 ? "text-warn" : ""}>Cash {money(r.cash)}</span>
          </div>
        </div>

        {!compact && (
          <div className="flex items-center justify-between gap-3 border-t border-hairline pt-3">
            <span className="text-xs text-mute">Over the pilot</span>
            <Sparkline data={r.trend} width={160} height={26} tone="ok" />
          </div>
        )}

        {!compact && (
          <NarrativeCard
            variant="interpretation"
            title="Why this matters"
            body={`Cash is where leakage hides. Routing spend through mobile money and bank rails makes every payment traceable; the share on traceable rails has climbed to ${formatPct(r.share)} this pilot. Cash remains in scope but small, and each cash spend carries a stated reason and proof of what was bought.`}
          />
        )}
      </CardBody>
    </Card>
  );
}
